import React from 'react';
import { observer } from 'mobx-react-lite';
import { PasswordConfig, PasswordPreset } from '../../types/password.types';
import { Accordion } from '../accordion/accordion';
import { SimpleModal } from '../simple-modal/simple-modal';
import './configuration-modal.scss';

interface ConfigurationFormProps {
  value: PasswordConfig | undefined;
  onChange: (value: PasswordConfig | undefined) => void;
  disabled: boolean;
  errors: any;
  presets: PasswordPreset[];
  passwordCount: number;
  useMemorable: boolean;
  onPasswordCountChange: (count: number) => void;
  onPresetApply: (preset: PasswordPreset) => void;
  onMemorableChange: (memorable: boolean) => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = observer(({
  value,
  onChange,
  disabled,
  presets,
  passwordCount,
  useMemorable,
  onPasswordCountChange,
  onPresetApply,
  onMemorableChange,
}) => {
  const config = value || {
    length: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeAmbiguous: false,
    minNumbers: 1,
    minSymbols: 1,
  };

  const updateConfig = (updates: Partial<PasswordConfig>) => {
    onChange({ ...config, ...updates });
  };

  // Presets section
  const presetsSection = (
    <div className="config-form__presets">
      <div className="config-form__presets-grid">
        {presets.map(preset => (
          <button
            key={preset.name}
            onClick={() => onPresetApply(preset)}
            className="config-form__preset-btn"
            title={preset.description}
            disabled={disabled}
          >
            <span className="config-form__preset-icon">{preset.icon}</span>
            <span className="config-form__preset-name">{preset.name}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Basic settings section
  const basicSettingsSection = (
    <div className="config-form__basic">
      <div className="config-form__field">
        <label className="config-form__label">
          Password Length: {config.length}
        </label>
        <input
          type="range"
          min="4"
          max="128"
          value={config.length}
          onChange={(e) => updateConfig({ length: parseInt(e.target.value) })}
          className="config-form__slider"
          disabled={disabled}
        />
        <div className="config-form__range-labels">
          <span>4</span>
          <span>128</span>
        </div>
      </div>

      <div className="config-form__field">
        <label className="config-form__label">
          Number of Passwords: {passwordCount}
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={passwordCount}
          onChange={(e) => onPasswordCountChange(parseInt(e.target.value))}
          className="config-form__slider"
          disabled={disabled}
        />
        <div className="config-form__range-labels">
          <span>1</span>
          <span>10</span>
        </div>
      </div>

      <div className="config-form__checkboxes">
        <label className="config-form__checkbox">
          <input
            type="checkbox"
            checked={useMemorable}
            onChange={(e) => onMemorableChange(e.target.checked)}
            disabled={disabled}
          />
          <span>Memorable Password (easier to remember)</span>
        </label>

        <label className="config-form__checkbox">
          <input
            type="checkbox"
            checked={config.includeUppercase}
            onChange={(e) => updateConfig({ includeUppercase: e.target.checked })}
            disabled={disabled}
          />
          <span>Uppercase Letters (A-Z)</span>
        </label>

        <label className="config-form__checkbox">
          <input
            type="checkbox"
            checked={config.includeLowercase}
            onChange={(e) => updateConfig({ includeLowercase: e.target.checked })}
            disabled={disabled}
          />
          <span>Lowercase Letters (a-z)</span>
        </label>

        <label className="config-form__checkbox">
          <input
            type="checkbox"
            checked={config.includeNumbers}
            onChange={(e) => updateConfig({ includeNumbers: e.target.checked })}
            disabled={disabled}
          />
          <span>Numbers (0-9)</span>
        </label>

        <label className="config-form__checkbox">
          <input
            type="checkbox"
            checked={config.includeSymbols}
            onChange={(e) => updateConfig({ includeSymbols: e.target.checked })}
            disabled={disabled}
          />
          <span>Symbols (!@#$%^&*)</span>
        </label>
      </div>
    </div>
  );

  // Advanced settings section
  const advancedSettingsSection = (
    <div className="config-form__advanced">
      <label className="config-form__checkbox">
        <input
          type="checkbox"
          checked={config.excludeAmbiguous}
          onChange={(e) => updateConfig({ excludeAmbiguous: e.target.checked })}
          disabled={disabled}
        />
        <span>Exclude Ambiguous Characters (0, O, l, I, |)</span>
      </label>

      {config.includeSymbols && (
        <div className="config-form__field">
          <label className="config-form__label">
            Custom Symbols (optional)
          </label>
          <input
            type="text"
            value={config.customSymbols || ''}
            onChange={(e) => updateConfig({ customSymbols: e.target.value })}
            placeholder="!@#$%^&*()_+-=[]{}|;:,.<>?"
            className="config-form__text-input"
            disabled={disabled}
          />
        </div>
      )}

      {config.includeNumbers && (
        <div className="config-form__field">
          <label className="config-form__label">
            Minimum Numbers: {config.minNumbers || 0}
          </label>
          <input
            type="range"
            min="0"
            max="8"
            value={config.minNumbers || 0}
            onChange={(e) => updateConfig({ minNumbers: parseInt(e.target.value) })}
            className="config-form__slider"
            disabled={disabled}
          />
        </div>
      )}

      {config.includeSymbols && (
        <div className="config-form__field">
          <label className="config-form__label">
            Minimum Symbols: {config.minSymbols || 0}
          </label>
          <input
            type="range"
            min="0"
            max="8"
            value={config.minSymbols || 0}
            onChange={(e) => updateConfig({ minSymbols: parseInt(e.target.value) })}
            className="config-form__slider"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );

  const accordionSections = [
    {
      id: 'presets',
      title: '⚡ Quick Presets',
      children: presetsSection,
    },
    {
      id: 'basic',
      title: '⚙️ Basic Settings',
      children: basicSettingsSection,
    },
    {
      id: 'advanced',
      title: '🔧 Advanced Settings',
      children: advancedSettingsSection,
    },
  ];

  return (
    <div className="config-form">
      <Accordion 
        sections={accordionSections} 
        defaultOpen={['presets', 'basic']}
        className="config-form__accordion"
      />
    </div>
  );
});

interface ConfigurationModalProps {
  config: PasswordConfig;
  presets: PasswordPreset[];
  passwordCount: number;
  useMemorable: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfigUpdate: (updates: Partial<PasswordConfig>) => void;
  onPasswordCountChange: (count: number) => void;
  onPresetApply: (preset: PasswordPreset) => void;
  onMemorableChange: (memorable: boolean) => void;
  onGenerate: () => void;
}

export const ConfigurationModal: React.FC<ConfigurationModalProps> = observer(({
  config,
  presets,
  passwordCount,
  useMemorable,
  isOpen,
  onClose,
  onConfigUpdate,
  onPasswordCountChange,
  onPresetApply,
  onMemorableChange,
  onGenerate,
}) => {
  const handleSubmit = async () => {
    await onGenerate();
    onClose();
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title="🔐 Password Generator Settings"
      onSubmit={handleSubmit}
      submitLabel="Generate Passwords"
      cancelLabel="Cancel"
    >
      <ConfigurationForm
        value={config}
        onChange={(value) => {
          if (value) {
            onConfigUpdate(value);
          }
        }}
        disabled={false}
        errors={{}}
        presets={presets}
        passwordCount={passwordCount}
        useMemorable={useMemorable}
        onPasswordCountChange={onPasswordCountChange}
        onPresetApply={onPresetApply}
        onMemorableChange={onMemorableChange}
      />
    </SimpleModal>
  );
});
